import { defineStore } from 'pinia'
import { CustomizationTemplate, FaceSwapped, StyleTransfered } from '@/types'
import Logger from '@/services/log'
import Provider from '@/provider'
import CacheService, { getJSONfromCacheResponse } from '@/services/cache'
import Analytics from '@/services/analytics'

export interface CustomizationState {
  customizationTemplates: CustomizationTemplate[]
  styleTransfered: StyleTransfered[]
  faceSwapped: FaceSwapped[]
}

const useCustomizationStore = defineStore('customization', {
  state: (): CustomizationState => ({
    customizationTemplates: [],
    styleTransfered: [],
    faceSwapped: [],
  }),
  actions: {
    async uploadImageTransferDefault({
      file,
      id,
      isForceLoadNewData,
    }: {
      file: File
      id: number | string
      isForceLoadNewData?: boolean
    }) {
      try {
        let imageUrl = null
        const payload = { file, id }
        const cacheKey = `customization_uploadImageTransferDefault_${file.name}_${file.type}_${file.size}_${id}`
        if (!isForceLoadNewData) {
          const cacheResponse = await CacheService.instance?.get(cacheKey)
          const imageUrlFromCache = getJSONfromCacheResponse(cacheResponse)

          if (imageUrlFromCache) {
            imageUrl = imageUrlFromCache
          }
        }

        if (!imageUrl) {
          const provider = await Provider.getInstance()
          const response = await provider.uploadImageTransferDefault(payload)
          if (response.success) {
            imageUrl = response.image
          } else {
            throw response
          }
          if (imageUrl) {
            CacheService.instance?.set(cacheKey, imageUrl)
          } else {
            CacheService.instance?.delete(cacheKey)
          }
        }

        return imageUrl
      } catch (error: any) {
        Logger.error('Error on uploadImage', {
          error,
          payload: { file, id },
        })
        Analytics.error(error)
        return error
      }
    },

    async transferImage({
      image,
      templateId,
      id,
      custom_name,
      isForceLoadNewData,
    }: {
      image: string
      templateId: string
      id: number
      custom_name: string
      isForceLoadNewData?: boolean
    }) {
      try {
        let imageTransfer = null
        const payload = { image, templateId, id, custom_name }

        const cacheKey = `customization_transferImage_${image}_${templateId}_${id}_${custom_name}`
        if (!isForceLoadNewData) {
          const cacheResponse = await CacheService.instance?.get(cacheKey)
          const imageTransferFromCache = getJSONfromCacheResponse(cacheResponse)

          if (imageTransferFromCache) {
            imageTransfer = imageTransferFromCache
          }
        }

        if (!imageTransfer) {
          const provider = await Provider.getInstance()
          imageTransfer = await provider.transferImage(payload)

          if (imageTransfer) {
            CacheService.instance?.set(cacheKey, imageTransfer)
          } else {
            CacheService.instance?.delete(cacheKey)
          }
        }
        return imageTransfer
      } catch (error: any) {
        Logger.error('Error on transferImage', {
          error,
          payload: { image, templateId, id },
        })
        Analytics.error(error)
        return null
      }
    },

    async getCustomizationTemplates({
      code,
      type,
      tag,
      isForceLoadNewData,
    }: {
      code: string
      type: string
      tag: string
      isForceLoadNewData?: boolean
    }) {
      try {
        let customizationTemplates = null
        const payload = { code, type, tag }

        const cacheKey = `customization_getCustomizationTemplates_${code}_${type}_${tag}`
        if (!isForceLoadNewData) {
          const cacheResponse = await CacheService.instance?.get(cacheKey)
          const customizationTemplatesFromCache =
            getJSONfromCacheResponse(cacheResponse)

          if (customizationTemplatesFromCache) {
            customizationTemplates = customizationTemplatesFromCache
          }
        }

        if (!customizationTemplates) {
          const provider = await Provider.getInstance()
          customizationTemplates = await provider.getCustomizationTemplates(
            payload
          )

          if (customizationTemplates) {
            CacheService.instance?.set(cacheKey, customizationTemplates)
          } else {
            CacheService.instance?.delete(cacheKey)
          }
        }

        return customizationTemplates
      } catch (error: any) {
        Logger.error('Error on getCustomizationTemplates', {
          error,
          payload: { code, type, tag },
        })
        Analytics.error(error)
        return null
      }
    },

    async fetchCustomizationTemplates({
      code,
      type,
      tag,
      isForceLoadNewData,
    }: {
      code: string
      type: string
      tag: string
      isForceLoadNewData?: boolean
    }) {
      this.customizationTemplates = []
      const customizationTemplates = await this.getCustomizationTemplates({
        code,
        type,
        tag,
        isForceLoadNewData,
      })
      this.customizationTemplates = customizationTemplates
      return customizationTemplates
    },

    async faceSwap({
      face_index,
      face_file,
      source_file,
    }: {
      face_index: number
      face_file: File
      source_file: File
    }) {
      try {
        let imageUrl = null
        let faceImageUrl = null
        const provider = await Provider.getInstance()
        const response = await provider.faceSwap({
          face_index,
          face_file,
          source_file,
        })
        if (response.success) {
          imageUrl = response.image
          faceImageUrl = response.face_image
        } else {
          throw response
        }

        return { image: imageUrl, face_image: faceImageUrl }
      } catch (error: any) {
        Logger.error('Error on faceSwap', {
          error,
          payload: { face_index, face_file, source_file },
        })
        Analytics.error(error)
        return error
      }
    },

    async searchSong({
      songName,
      appName,
      nextPage,
      isForceLoadNewData,
    }: {
      songName?: string
      appName?: string
      nextPage?: string
      isForceLoadNewData?: boolean
    }) {
      try {
        let listSong = []

        const cacheKey = `customization_searchSong_${songName}_${appName}_${nextPage}`
        if (!isForceLoadNewData) {
          const cacheResponse = await CacheService.instance?.get(cacheKey)
          const listSongFromCache = getJSONfromCacheResponse(cacheResponse)

          if (listSongFromCache) {
            listSong = listSongFromCache
          }
        }

        if (!listSong.length) {
          const provider = await Provider.getInstance()
          listSong = await provider.searchSong({
            songName,
            appName,
            nextPage,
          })

          if (listSong.length) {
            CacheService.instance?.set(cacheKey, listSong)
          } else {
            CacheService.instance?.delete(cacheKey)
          }
        }
        return listSong
      } catch (error: any) {
        Logger.error('Error on searchSong', {
          error,
          payload: { songName, appName, nextPage },
        })
        Analytics.error(error)
        return null
      }
    },

    async generateQrCodeByFile({
      file,
      color,
      isForceLoadNewData,
    }: {
      file: File
      color: string
      isForceLoadNewData?: boolean
    }) {
      try {
        let imageQrCodeUrl = null

        const cacheKey = `customization_generateQrCodeByFile_${file.name}_${file.type}_${file.size}_${color}`
        if (!isForceLoadNewData) {
          const cacheResponse = await CacheService.instance?.get(cacheKey)
          const imageQrCodeUrlFromCache =
            getJSONfromCacheResponse(cacheResponse)

          if (imageQrCodeUrlFromCache) {
            imageQrCodeUrl = imageQrCodeUrlFromCache
          }
        }

        if (!imageQrCodeUrl) {
          const provider = await Provider.getInstance()
          const response = await provider.generateQrCodeByFile(file, color)
          if (response.success) {
            imageQrCodeUrl = response.image
          } else {
            throw response
          }
          if (imageQrCodeUrl) {
            CacheService.instance?.set(cacheKey, imageQrCodeUrl)
          } else {
            CacheService.instance?.delete(cacheKey)
          }
        }

        return imageQrCodeUrl
      } catch (error: any) {
        Logger.error('Error on generateQrCodeByFile', {
          error,
          payload: { file, color },
        })
        Analytics.error(error)
        return error
      }
    },

    async generateQrCodeByUri({
      uri,
      color,
      isForceLoadNewData,
    }: {
      uri: string
      color: string
      isForceLoadNewData?: boolean
    }) {
      try {
        let imageQrCodeUrl = null

        const cacheKey = `customization_generateQrCodeByUri_${uri}_${color}`
        if (!isForceLoadNewData) {
          const cacheResponse = await CacheService.instance?.get(cacheKey)
          const imageQrCodeUrlFromCache =
            getJSONfromCacheResponse(cacheResponse)

          if (imageQrCodeUrlFromCache) {
            imageQrCodeUrl = imageQrCodeUrlFromCache
          }
        }

        if (!imageQrCodeUrl) {
          const provider = await Provider.getInstance()
          const response = await provider.generateQrCodeByUri(uri, color)
          if (response.success) {
            imageQrCodeUrl = response.image
          } else {
            throw response
          }
          if (imageQrCodeUrl) {
            CacheService.instance?.set(cacheKey, imageQrCodeUrl)
          } else {
            CacheService.instance?.delete(cacheKey)
          }
        }

        return imageQrCodeUrl
      } catch (error: any) {
        Logger.error('Error on generateQrCodeByUri', {
          error,
          payload: { uri, color },
        })
        Analytics.error(error)
        return error
      }
    },

    async generateWaveForm({
      type,
      file,
      url,
      isForceLoadNewData,
    }: {
      type: string
      file?: File
      url?: string
      isForceLoadNewData?: boolean
    }) {
      try {
        let waveForm = null
        let cacheKey = ''
        if (type == 'file' && file) {
          cacheKey = `customization_generateWaveForm_${file.name}_${file.type}_${file.size}`
        } else if (type == 'url' && url) {
          cacheKey = `customization_generateWaveForm_${url}`
        }

        if (!isForceLoadNewData) {
          const cacheResponse = await CacheService.instance?.get(cacheKey)
          const waveFormFromCache = getJSONfromCacheResponse(cacheResponse)

          if (waveFormFromCache) {
            waveForm = waveFormFromCache
          }
        }

        if (!waveForm) {
          const provider = await Provider.getInstance()
          const response = await provider.generateWaveForm({ type, file, url })
          if (response.success) {
            waveForm = response.data
          } else {
            throw response
          }
          if (waveForm) {
            CacheService.instance?.set(cacheKey, waveForm)
          } else {
            CacheService.instance?.delete(cacheKey)
          }
        }

        return waveForm
      } catch (error: any) {
        Logger.error('Error on generateWaveForm', {
          error,
          payload: { type, file, url },
        })
        Analytics.error(error)
        return error
      }
    },
  },
})

export default useCustomizationStore
